// The content calendar. One tile per DAY, whether or not anything is on it.
//
// WHY A DAY GRID AND NOT A LIST OF POSTS. A calendar assembled by grouping the
// content board can only ever draw the days that have content, which makes the
// days with none invisible. Those are the only days this screen exists to show.
// So v_content_calendar generates the days and joins the posts onto them, and a
// weekday with nothing scheduled arrives as a row with is_gap true.
//
// The standard is at least one piece of content on every weekend-free day.
// Weekends are drawn flat and are never a gap, because nothing is owed on them.
import { useEffect, useMemo } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Spinner } from '../../lib/shadcn/spinner'
import { cn } from '../../lib/shadcn/utils'

export type CalendarDay = {
  on_date: string
  iso_dow: number
  is_weekday: boolean
  is_today: boolean
  is_past: boolean
  item_count: number
  items: {
    id: string
    title: string
    state: string
    channel: string | null
    format: string | null
    audience: string | null
    task_ref: number | null
    published_at: string | null
  }[]
  is_gap: boolean
  gap_task_ref: number | null
  gap_task_status: string | null
}

// The same five states the content board uses, drawn the same way, so a post
// does not change colour when you look at it on a different screen.
const STATE_TONE: Record<string, string> = {
  Draft: 'bg-muted text-muted-foreground',
  Submitted: 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200',
  'Changes requested': 'bg-rose-100 text-rose-900 dark:bg-rose-950 dark:text-rose-200',
  Approved: 'bg-sky-100 text-sky-900 dark:bg-sky-950 dark:text-sky-200',
  Published: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200',
}

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

function monthLabel(iso: string) {
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' })
}

export function ContentCalendar({
  days,
  loading,
  error,
  onOpenItem,
  onReload,
}: {
  days: CalendarDay[] | null
  loading: boolean
  error: string | null
  onOpenItem: (id: string) => void
  onReload: () => void
}) {
  useEffect(() => {
    if (days === null && !loading) onReload()
  }, [days, loading, onReload])

  // Grouped into weeks by the row's own iso_dow rather than by counting, so a
  // window that does not start on a Monday cannot silently shift every tile one
  // column to the left.
  const weeks = useMemo(() => {
    const out: CalendarDay[][] = []
    let cur: CalendarDay[] = []
    for (const d of days ?? []) {
      if (d.iso_dow === 1 && cur.length > 0) {
        out.push(cur)
        cur = []
      }
      cur.push(d)
    }
    if (cur.length > 0) out.push(cur)
    return out
  }, [days])

  const gapCount = useMemo(
    () => (days ?? []).filter((d) => d.is_gap && !d.is_past).length,
    [days],
  )

  if (loading && days === null) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded border border-rose-300 bg-rose-50 p-4 text-sm text-rose-900 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200">
        {error}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {/* The headline is the count of days with nothing on them, not the count
          of posts. A calendar that opens by telling you how much content you
          have is answering the comfortable question. */}
      <div
        className={cn(
          'flex items-center gap-2 rounded border px-3 py-2 text-sm',
          gapCount === 0
            ? 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200'
            : 'border-rose-300 bg-rose-50 text-rose-900 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200',
        )}
      >
        {gapCount > 0 && <AlertTriangle className="h-4 w-4 shrink-0" />}
        <span>
          {gapCount === 0
            ? 'Every weekday ahead has content planned.'
            : `${gapCount} weekday${gapCount === 1 ? '' : 's'} ahead ${
                gapCount === 1 ? 'has' : 'have'
              } nothing planned.`}
        </span>
        <span className="ml-auto text-xs opacity-80">
          A draft scheduled for the day counts as planned. Getting it out is the content card&rsquo;s job.
        </span>
      </div>

      <div className="grid grid-cols-7 gap-1 text-[11px] font-medium text-muted-foreground">
        {DOW.map((d) => (
          <div key={d} className="px-1">
            {d}
          </div>
        ))}
      </div>

      <div className="space-y-1">
        {weeks.map((week) => {
          const first = week[0]
          const showsMonth = week.some((d) => Number(d.on_date.slice(8, 10)) <= 7)
          return (
            <div key={first.on_date}>
              {showsMonth && (
                <div className="px-1 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {monthLabel(week.find((d) => Number(d.on_date.slice(8, 10)) <= 7)!.on_date)}
                </div>
              )}
              <div className="grid grid-cols-7 gap-1">
                {week.map((d) => (
                  <div
                    key={d.on_date}
                    className={cn(
                      'min-h-[86px] rounded border p-1.5 text-xs',
                      !d.is_weekday && 'bg-muted/40',
                      d.is_gap && !d.is_past &&
                        'border-rose-400 bg-rose-50 dark:border-rose-800 dark:bg-rose-950/30',
                      d.is_gap && d.is_past &&
                        'border-dashed border-rose-300 bg-transparent dark:border-rose-900',
                      d.is_today && 'ring-2 ring-primary',
                      d.is_past && 'opacity-70',
                    )}
                  >
                    <div className="flex items-baseline justify-between">
                      <span className={cn('tabular-nums font-medium', d.is_today && 'text-primary')}>
                        {Number(d.on_date.slice(8, 10))}
                      </span>
                      {d.gap_task_ref !== null &&
                        d.gap_task_status !== 'Done' &&
                        d.gap_task_status !== 'Cancelled' && (
                          <span
                            className="text-[10px] text-rose-700 dark:text-rose-300"
                            title="A task has been raised for this empty day."
                          >
                            #{d.gap_task_ref}
                          </span>
                        )}
                    </div>

                    {d.items.length === 0 ? (
                      <div className="mt-1 text-[10px] italic text-muted-foreground">
                        {d.is_weekday ? (d.is_past ? 'nothing went out' : 'nothing planned') : ''}
                      </div>
                    ) : (
                      <div className="mt-1 space-y-0.5">
                        {d.items.map((it) => (
                          <button
                            key={it.id}
                            type="button"
                            onClick={() => onOpenItem(it.id)}
                            title={`${it.title} — ${it.state}${it.channel ? ' — ' + it.channel : ''}`}
                            className={cn(
                              'block w-full truncate rounded px-1 py-0.5 text-left text-[10px] hover:opacity-80',
                              STATE_TONE[it.state] ?? 'bg-muted text-muted-foreground',
                            )}
                          >
                            {it.title}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
